import { inject, Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  AbortError,
  HttpError,
  HubConnection,
  HubConnectionState,
  LogLevel,
  TimeoutError,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { DateTimeService } from '@application/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import {
  ACADEMY_LIVE_HUB_PATH,
  AcademyHubEvents,
  ChatMessageAddedEvent,
  ChatMessageRemovedEvent,
  ChatParticipantMuteChangedEvent,
  coerceEnum,
  EventStatusChangedEvent,
  HubRejection,
  HubRejoin,
  HubSendRejection,
  HubStatus,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  QuestionAddedEvent,
  QuestionStatusChangedEvent,
  QuestionUpvotedEvent,
  ReactionBroadcastEvent,
  ReactionCountsChangedEvent,
  ViewerCountChangedEvent,
} from '../entities/academy-realtime.model';
import {
  ACADEMY_ENUM_WIRE_CODES,
  LIVE_EVENT_STATUS_BY_NUMBER,
  LIVE_QUESTION_STATUS_BY_NUMBER,
  REACTION_TYPE_BY_NUMBER,
  ReactionType,
} from '../entities/academy-enums.model';
import { ReactionCounts } from '../entities/academy-responses.model';
import { AcademyReconnectPolicy } from './academy-reconnect.policy';

/**
 * SignalR client for the academy live hub (`/hubs/live`). Mirrors
 * `LivePickupRealtimeService`: token from `LocalStorageFacade`, exp-backoff reconnect,
 * group re-join after reconnect (the hub drops membership). Payloads are camelCase;
 * each enum field passes through `coerceEnum` (name **or** number) before emit.
 * Client→server wrappers no-op when the connection is not Connected.
 */
@Injectable({ providedIn: 'root' })
export class AcademyRealtimeService implements OnDestroy {
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;
  private readonly localStorageFacade = inject(LocalStorageFacade);
  private readonly dateTimeService = inject(DateTimeService);

  private connection: HubConnection | null = null;
  private isStarting = false;
  /**
   * The last joined event and how it was joined, so a reconnect re-joins the same way: a
   * producer studio must come back through `JoinAsProducer`, or it would open an
   * attendance and count as a viewer.
   */
  private lastJoin: { liveEventId: string; asProducer: boolean } | null = null;
  /**
   * One policy for the life of the service, not one per connection: it has to remember how the
   * previous connections ended to tell a drop worth retrying at once from a hub that closes
   * every connection it accepts. Timed on the monotonic clock — these are durations, and the
   * wall clock is allowed to jump.
   */
  protected readonly reconnectPolicy = new AcademyReconnectPolicy(() => performance.now());

  private readonly hubStatusSubject = new BehaviorSubject<HubStatus>('disconnected');
  public readonly hubStatus$: Observable<HubStatus> = this.hubStatusSubject.asObservable();

  private readonly eventStatusChangedSubject = new Subject<EventStatusChangedEvent>();
  public readonly eventStatusChanged$ = this.eventStatusChangedSubject.asObservable();

  private readonly viewerCountChangedSubject = new Subject<ViewerCountChangedEvent>();
  public readonly viewerCountChanged$ = this.viewerCountChangedSubject.asObservable();

  private readonly questionAddedSubject = new Subject<QuestionAddedEvent>();
  public readonly questionAdded$ = this.questionAddedSubject.asObservable();

  private readonly questionUpvotedSubject = new Subject<QuestionUpvotedEvent>();
  public readonly questionUpvoted$ = this.questionUpvotedSubject.asObservable();

  private readonly questionStatusChangedSubject = new Subject<QuestionStatusChangedEvent>();
  public readonly questionStatusChanged$ = this.questionStatusChangedSubject.asObservable();

  private readonly chatMessageAddedSubject = new Subject<ChatMessageAddedEvent>();
  public readonly chatMessageAdded$ = this.chatMessageAddedSubject.asObservable();

  private readonly chatMessageRemovedSubject = new Subject<ChatMessageRemovedEvent>();
  public readonly chatMessageRemoved$ = this.chatMessageRemovedSubject.asObservable();

  private readonly reactionBroadcastSubject = new Subject<ReactionBroadcastEvent>();
  public readonly reactionBroadcast$ = this.reactionBroadcastSubject.asObservable();

  private readonly reactionCountsChangedSubject = new Subject<ReactionCountsChangedEvent>();
  public readonly reactionCountsChanged$ = this.reactionCountsChangedSubject.asObservable();

  private readonly participantJoinedSubject = new Subject<ParticipantJoinedEvent>();
  public readonly participantJoined$ = this.participantJoinedSubject.asObservable();

  private readonly participantLeftSubject = new Subject<ParticipantLeftEvent>();
  public readonly participantLeft$ = this.participantLeftSubject.asObservable();

  private readonly chatParticipantMuteChangedSubject =
    new Subject<ChatParticipantMuteChangedEvent>();
  public readonly chatParticipantMuteChanged$ =
    this.chatParticipantMuteChangedSubject.asObservable();

  /**
   * A join the hub itself refused — the audience gate, or the producer one. Final: nothing is
   * retried, and the watch page raises its banner on it. Only a refusal the hub pronounced
   * belongs here; a join the connection cut short goes out on `rejoinRejected$`.
   */
  private readonly joinRejectedSubject = new Subject<HubRejection>();
  public readonly joinRejected$ = this.joinRejectedSubject.asObservable();

  /**
   * A join — first one or re-join after a reconnect — that the connection cut short, kept apart
   * from `joinRejected$` on purpose. The two look alike and are not: a refusal is pronounced by
   * the audience (or producer) gate and is final, while this one is the connection dropping
   * mid-invoke and the next reconnect recovers it. Folding it into `joinRejected$` made the
   * watch page show "you are not in this live's audience" over a transient failure, and never
   * take it back. `lastJoin` is kept in this case, so the reconnect has a group to go back to.
   */
  private readonly rejoinRejectedSubject = new Subject<HubRejection>();
  public readonly rejoinRejected$ = this.rejoinRejectedSubject.asObservable();

  /**
   * A message or reaction that did not go out — refused by the hub (audience, chat closed,
   * mute, rate limit) or with no connection to send it on. Surfaced, never thrown.
   */
  private readonly sendRejectedSubject = new Subject<HubSendRejection>();
  public readonly sendRejected$ = this.sendRejectedSubject.asObservable();

  /**
   * Emitted after the last group was joined again — by a reconnect, or by a heartbeat the hub
   * answered with a closed presence session. Never on the first connection.
   */
  private readonly rejoinedSubject = new Subject<HubRejoin>();
  public readonly rejoined$ = this.rejoinedSubject.asObservable();

  /** What the hub's answer to a `JoinEvent` carries when the audience gate refuses the viewer. */
  private readonly notAuthorizedText = 'Not authorized for this event';

  /** Pause before trying again a viewer join the server failed: 2s, doubling, up to 30s. */
  private readonly joinRetryBaseMs = 2000;
  private readonly joinRetryMaxMs = 30000;
  private joinRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private joinRetryCount = 0;

  public async start(): Promise<void> {
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected ||
      this.connection?.state === HubConnectionState.Connecting
    ) {
      return;
    }

    this.isStarting = true;
    this.emitStatus('connecting');

    try {
      if (this.connection) await this.stop();

      this.connection = this.buildConnection(`${this.baseUrl}${ACADEMY_LIVE_HUB_PATH}`);
      this.setupHandlers();

      await this.connection.start();
      this.reconnectPolicy.connected();
      this.emitStatus('connected');
    } catch (error) {
      this.emitStatus('disconnected');
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  public async stop(): Promise<void> {
    this.cancelJoinRetry();
    this.reconnectPolicy.reset();
    this.emitStatus('disconnected');
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {
        // ignore
      }
      this.connection = null;
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public async joinEvent(liveEventId: string): Promise<void> {
    this.cancelJoinRetry();
    this.lastJoin = { liveEventId, asProducer: false };
    try {
      await this.invoke('JoinEvent', liveEventId);
    } catch (error) {
      this.surfaceJoinFailure(liveEventId, false, error);
    }
  }

  public async leaveEvent(liveEventId: string, watchedSeconds: number): Promise<void> {
    this.cancelJoinRetry();
    this.lastJoin = null;
    await this.invoke('LeaveEvent', liveEventId, watchedSeconds);
  }

  /**
   * Producer studio join (backend 013): status/viewer aggregates plus the per-person
   * `ParticipantJoined/Left` events, without opening an attendance or counting as a viewer.
   * A non-producer is rejected by the hub and surfaced as `joinRejected$`, like `joinEvent`;
   * a connection that drops mid-invoke goes out on `rejoinRejected$`, also like `joinEvent`.
   */
  public async joinAsProducer(liveEventId: string): Promise<void> {
    this.cancelJoinRetry();
    this.lastJoin = { liveEventId, asProducer: true };
    try {
      await this.invoke('JoinAsProducer', liveEventId);
    } catch (error) {
      this.surfaceJoinFailure(liveEventId, true, error);
    }
  }

  /**
   * Tells the two failures a join can end in apart — the first one as much as the re-join a
   * reconnect issues — because they call for opposite reactions and only one of them is a gate
   * refusing the caller.
   *
   * A refusal travels as a completion message carrying `error`, which only reaches a connection
   * that is still up; the SignalR client turns it into a plain `Error`
   * (`@microsoft/signalr/dist/cjs/HubConnection.js:353`). The connection dropping under the
   * invoke takes the other road: the pending callback is rejected by `_cancelCallbacksWithError`
   * (`:648`) and, in the same synchronous pass, the state moves to `Reconnecting` (`:696`) or
   * `Disconnected` (`:669`) — so by the time this `catch` runs, one microtask later, the state
   * already says the hub never answered. A transport that cannot even send rejects with a value
   * of its own: the WebSocket one with a bare string (`WebSocketTransport.js:121`), the HTTP
   * ones with `HttpError` / `TimeoutError` / `AbortError`.
   *
   * That leaves the hub's answer, and for a viewer it comes in two kinds a plain `Error` does
   * not tell apart: the audience gate saying no, and the server failing under the join (the
   * database away for a moment). Read the same, the second put "you are not in this live's
   * audience" in front of someone who is, for good. They are told apart by the text, the only
   * thing that differs: the refusal's is contract and pinned by a test on the backend
   * (academyservice-api round 21, `LiveHub.Errors.NotAuthorized`); anything else is a fault,
   * and a fault is tried again.
   *
   * A producer join keeps the older rule — any answer is a refusal. Its texts are not contract,
   * and guessing at them is how a refusal would end up retried forever.
   */
  private surfaceJoinFailure(liveEventId: string, asProducer: boolean, error: unknown): void {
    if (this.isConnectionFailure(error)) {
      // No gate spoke, so nothing was refused: `lastJoin` is kept, so the reconnect already
      // under way re-joins this very group, and the failure goes out on the subject that does
      // not raise the watch page's banner. Forgetting the join here is what left a viewer whose
      // connection blinked behind "you are not in this live's audience", in no group at all,
      // with no re-join ever coming to take it back.
      this.rejoinRejectedSubject.next({ liveEventId });
      return;
    }
    if (!asProducer && !this.isAudienceRefusal(error)) {
      // The server failed; nobody was refused. The room is kept, no banner is raised, and with
      // the connection up there is no reconnect coming to re-join it — so this does.
      this.rejoinRejectedSubject.next({ liveEventId });
      this.scheduleJoinRetry();
      return;
    }
    this.forgetJoin(liveEventId, asProducer);
    this.joinRejectedSubject.next({ liveEventId });
  }

  private isAudienceRefusal(error: unknown): boolean {
    return error instanceof Error && error.message.includes(this.notAuthorizedText);
  }

  /**
   * Tries the remembered join again after a pause that doubles while the fault lasts. One
   * timer at most; a join that goes through, a new join, leaving or stopping clears it. If the
   * connection is down when it fires it does nothing: the reconnect re-joins by itself.
   */
  private scheduleJoinRetry(): void {
    if (this.joinRetryTimer) clearTimeout(this.joinRetryTimer);
    const delay = Math.min(
      this.joinRetryBaseMs * Math.pow(2, this.joinRetryCount),
      this.joinRetryMaxMs,
    );
    this.joinRetryCount += 1;
    this.joinRetryTimer = setTimeout(() => {
      this.joinRetryTimer = null;
      if (this.connection?.state !== HubConnectionState.Connected) return;
      void this.rejoinLastGroup();
    }, delay);
  }

  private cancelJoinRetry(): void {
    if (this.joinRetryTimer) clearTimeout(this.joinRetryTimer);
    this.joinRetryTimer = null;
    this.joinRetryCount = 0;
  }

  /** See `surfaceJoinFailure` for why each of these three means the hub never answered. */
  private isConnectionFailure(error: unknown): boolean {
    if (this.connection?.state !== HubConnectionState.Connected) return true;
    return (
      !(error instanceof Error) ||
      error instanceof HttpError ||
      error instanceof TimeoutError ||
      error instanceof AbortError
    );
  }

  /**
   * Forgets the remembered join only if it is still the rejected one: a rejection that lands
   * after the user already joined another live must not stop that live from re-joining on
   * reconnect.
   */
  private forgetJoin(liveEventId: string, asProducer: boolean): void {
    if (this.lastJoin?.liveEventId === liveEventId && this.lastJoin.asProducer === asProducer) {
      this.lastJoin = null;
    }
  }

  /** Leaves the producer groups only — unlike `leaveEvent`, it closes no attendance. */
  public async leaveAsProducer(liveEventId: string): Promise<void> {
    this.cancelJoinRetry();
    this.lastJoin = null;
    await this.invoke('LeaveAsProducer', liveEventId);
  }

  /**
   * Keeps this connection's presence session alive. When the hub answers that the session is
   * already closed — the event went back to Scheduled, or the 90s sweep took a session whose
   * beats a background tab was delaying — the event is joined again, the same way a reconnect
   * re-joins it: the hub opens a new session, and `rejoined$` tells the page to count it from
   * zero. The hub keeps answering that on every beat until a `JoinEvent` replaces the session
   * (academyservice-api round 20), so a re-join that did not go through is tried again by the
   * next one.
   *
   * That answer is the `false` `Heartbeat` returns (round 22). Only a strict `false` counts:
   * the shared `invoke` resolves with nothing when there is no connection to send on, and that
   * does not say the session is closed. A failure is passed on untouched, whatever its text.
   */
  public async heartbeat(liveEventId: string, watchedSeconds: number): Promise<void> {
    const sessionOpen = await this.invoke<boolean>('Heartbeat', liveEventId, watchedSeconds);
    if (sessionOpen !== false || !this.isJoinedAsViewer(liveEventId)) return;
    await this.rejoinLastGroup();
  }

  /**
   * A closed session is only re-joined for the event this connection joined as a viewer: a
   * producer connection has no presence session, and an event already left has nothing to
   * re-join.
   */
  private isJoinedAsViewer(liveEventId: string): boolean {
    return this.lastJoin?.liveEventId === liveEventId && !this.lastJoin.asProducer;
  }

  /**
   * Sends a chat message. One that does not go out is announced on `sendRejected$`, so the
   * page can tell whoever wrote it — the box is already empty by then. That covers the hub
   * refusing it and the server failing, and also there being no connection: the shared
   * `invoke` drops a call quietly while disconnected, which for a typed message is a loss
   * nobody hears about.
   */
  public async sendMessage(liveEventId: string, text: string): Promise<void> {
    if (!this.isConnected()) {
      this.sendRejectedSubject.next({ liveEventId, kind: 'message' });
      return;
    }
    try {
      await this.invoke('SendMessage', liveEventId, text);
    } catch {
      this.sendRejectedSubject.next({ liveEventId, kind: 'message' });
    }
  }

  /**
   * The hub takes the reaction as its numeric wire code, from the same table the REST
   * writes use — the academy service has no string-enum converter. Measured against the
   * deployed hub on 2026-09-17: `5` is accepted and broadcast back, while `'Laugh'` answers
   * "Failed to invoke 'SendReaction' due to an error on the server". The contract doc that
   * says "by name" describes an intent the service never had.
   */
  public async sendReaction(liveEventId: string, reactionType: ReactionType): Promise<void> {
    try {
      await this.invoke(
        'SendReaction',
        liveEventId,
        ACADEMY_ENUM_WIRE_CODES['ReactionType'][reactionType],
      );
    } catch {
      this.sendRejectedSubject.next({ liveEventId, kind: 'reaction' });
    }
  }

  /** Builder seam — overridden in tests with a fake `HubConnection`. */
  protected buildConnection(url: string): HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(url, {
        // Hub auth is token-based (accessTokenFactory), not cookies — credentials aren't needed.
        // Keeping this false relaxes the negotiate CORS on /hubs/live (avoids the forbidden
        // `*` + credentials combination, so the backend can allow the origin without credentials).
        withCredentials: false,
        accessTokenFactory: () => this.readToken(),
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect(this.reconnectPolicy)
      .build();
  }

  private async rejoinLastGroup(): Promise<void> {
    const last = this.lastJoin;
    if (!last) return;
    try {
      await this.invoke(last.asProducer ? 'JoinAsProducer' : 'JoinEvent', last.liveEventId);
      this.cancelJoinRetry();
      this.rejoinedSubject.next({ liveEventId: last.liveEventId, asProducer: last.asProducer });
    } catch (error) {
      // The same three proofs the first join is read by: nothing about a re-join makes the hub's
      // "no" less of a refusal, and the connection dropping again is no more of one here than
      // there. Emitting the transient subject unconditionally, as this used to, cost both sides.
      // A drop keeps `lastJoin` and goes out on `rejoinRejected$` — which raises no banner — so
      // the next reconnect re-joins this very group. A refusal forgets it and goes out on
      // `joinRejected$`, which is the only thing that would ever tell: `onreconnected` emitted
      // 'connected' before re-joining, so the badge says connected while the connection sits in
      // no group at all. Without it, a viewer whose access was withdrawn while the connection
      // blinked kept a room that no longer exists, in front of a page that looks intact and
      // never receives anything again.
      this.surfaceJoinFailure(last.liveEventId, last.asProducer, error);
    }
  }

  /** Resolves with what the hub method returns — nothing when there is no connection. */
  private async invoke<T = void>(method: string, ...args: unknown[]): Promise<T | undefined> {
    if (this.connection?.state !== HubConnectionState.Connected) return undefined;
    return this.connection.invoke<T>(method, ...args);
  }

  private readToken(): string {
    try {
      const raw = this.localStorageFacade.getItem('authorization');
      if (!raw) return '';
      const auth = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return auth?.Token ?? '';
    } catch {
      return '';
    }
  }

  private setupHandlers(): void {
    if (!this.connection) return;
    const c = this.connection;

    c.off('EventStatusChanged');
    c.on('EventStatusChanged', (p: AcademyHubEvents['EventStatusChanged']) =>
      this.eventStatusChangedSubject.next({
        liveEventId: p.liveEventId,
        status: coerceEnum(p.status, LIVE_EVENT_STATUS_BY_NUMBER) ?? p.status,
      }),
    );

    c.off('ViewerCountChanged');
    c.on('ViewerCountChanged', (p: ViewerCountChangedEvent) =>
      this.viewerCountChangedSubject.next(p),
    );

    c.off('QuestionAdded');
    c.on('QuestionAdded', (p: AcademyHubEvents['QuestionAdded']) =>
      this.questionAddedSubject.next({
        ...p,
        status: coerceEnum(p.status, LIVE_QUESTION_STATUS_BY_NUMBER) ?? p.status,
      }),
    );

    c.off('QuestionUpvoted');
    c.on('QuestionUpvoted', (p: QuestionUpvotedEvent) =>
      this.questionUpvotedSubject.next(p),
    );

    c.off('QuestionStatusChanged');
    c.on('QuestionStatusChanged', (p: AcademyHubEvents['QuestionStatusChanged']) =>
      this.questionStatusChangedSubject.next({
        ...p,
        status: coerceEnum(p.status, LIVE_QUESTION_STATUS_BY_NUMBER) ?? p.status,
      }),
    );

    // `sentOnUtc` bypasses the `DateInterceptor` over the hub: converted once, on arrival, to the
    // same account wall-clock form the REST chat history carries.
    c.off('ChatMessageAdded');
    c.on('ChatMessageAdded', (p: ChatMessageAddedEvent) =>
      this.chatMessageAddedSubject.next(this.dateTimeService.toLocalObject(p)),
    );

    c.off('ChatMessageRemoved');
    c.on('ChatMessageRemoved', (p: ChatMessageRemovedEvent) =>
      this.chatMessageRemovedSubject.next(p),
    );

    c.off('ReactionBroadcast');
    c.on('ReactionBroadcast', (p: AcademyHubEvents['ReactionBroadcast']) =>
      this.reactionBroadcastSubject.next({
        ...p,
        reactionType: coerceEnum(p.reactionType, REACTION_TYPE_BY_NUMBER) ?? p.reactionType,
      }),
    );

    c.off('ReactionCountsChanged');
    c.on('ReactionCountsChanged', (p: AcademyHubEvents['ReactionCountsChanged']) =>
      this.reactionCountsChangedSubject.next({
        liveEventId: p.liveEventId,
        counts: this.coerceCounts(p.counts),
      }),
    );

    // Hub payloads bypass the `DateInterceptor`, so their `*Utc` instants are brought to the
    // account's wall clock here, once, on arrival — the same form the REST roster rows carry.
    c.off('ParticipantJoined');
    c.on('ParticipantJoined', (p: ParticipantJoinedEvent) =>
      this.participantJoinedSubject.next(this.dateTimeService.toLocalObject(p)),
    );

    c.off('ParticipantLeft');
    c.on('ParticipantLeft', (p: ParticipantLeftEvent) =>
      this.participantLeftSubject.next(this.dateTimeService.toLocalObject(p)),
    );

    c.off('ChatParticipantMuteChanged');
    c.on('ChatParticipantMuteChanged', (p: ChatParticipantMuteChangedEvent) =>
      this.chatParticipantMuteChangedSubject.next(p),
    );

    c.onclose(() => this.emitStatus('disconnected'));
    c.onreconnecting(() => this.emitStatus('reconnecting'));
    c.onreconnected(() => {
      this.reconnectPolicy.connected();
      this.emitStatus('connected');
      void this.rejoinLastGroup();
    });
  }

  /** Reaction maps may arrive keyed by enum number; normalize keys to ReactionType names. */
  private coerceCounts(counts: Record<string, number> | ReactionCounts): ReactionCounts {
    const out: ReactionCounts = {};
    for (const [key, value] of Object.entries(counts ?? {})) {
      const name = coerceEnum<ReactionType>(key, REACTION_TYPE_BY_NUMBER);
      if (name) out[name] = value;
    }
    return out;
  }

  private emitStatus(status: HubStatus): void {
    this.hubStatusSubject.next(status);
  }

  ngOnDestroy(): void {
    void this.stop();
    this.hubStatusSubject.complete();
    this.eventStatusChangedSubject.complete();
    this.viewerCountChangedSubject.complete();
    this.questionAddedSubject.complete();
    this.questionUpvotedSubject.complete();
    this.questionStatusChangedSubject.complete();
    this.chatMessageAddedSubject.complete();
    this.chatMessageRemovedSubject.complete();
    this.reactionBroadcastSubject.complete();
    this.reactionCountsChangedSubject.complete();
    this.participantJoinedSubject.complete();
    this.participantLeftSubject.complete();
    this.chatParticipantMuteChangedSubject.complete();
    this.joinRejectedSubject.complete();
    this.rejoinRejectedSubject.complete();
    this.sendRejectedSubject.complete();
    this.rejoinedSubject.complete();
  }
}
